import React, {
    useEffect,
    useMemo,
    useRef,
    useCallback,
    memo,
    useLayoutEffect,
} from "react";
import {
    EsriPortalUrl,
    EsriApiKey,
    EsriPortalMapId,
    EsriPortalSceneId,
} from "src/M_Components/M_Utils/url.service";

/**
 * EsriGenericMap
 *
 * React wrapper for the <esri-map> Web Component.
 *
 * What this component does:
 * - Creates a stable and deterministic mapConfig object and assigns it to the custom element (el.mapConfig)
 * - Keeps event callbacks stable (no stale closures) by storing the latest callbacks in refs
 * - Registers and unregisters web-component listeners safely
 * - Performs defensive cleanup to release WebGL resources when the element is replaced/unmounted
 * - Optionally injects a "Locations" overlay layer and supports picking a location by clicking features
 *
 * Performance strategy (Jan 2026):
 * - Parent screens may update "marker-like" layers frequently (e.g., a single point moving).
 * - Reassigning full mapConfig for every tiny change is expensive and can cause internal cancellations.
 * - This wrapper reduces churn by:
 *   - Debouncing mapConfig assignment (configDebounceMs)
 *   - Skipping redundant assignments using a deterministic signature (configSignature)
 *   - Including a lightweight "marker signature" for simple GeoJSON point layers (cheap movement detection)
 *
 * AbortError strategy:
 * - ArcGIS / Esri operations are often cancellable (hitTest, selection, reverse geocode, gestures, etc.).
 * - Cancellations frequently reject Promises with AbortError (or message "Aborted").
 * - AbortError is expected control-flow and should not surface as a red runtime overlay.
 *
 * IMPORTANT:
 * - Some AbortErrors originate inside the Web Component internals, outside our direct mapAction calls.
 * - To prevent noisy "Uncaught (in promise) AbortError", install a scoped window
 *   "unhandledrejection" listener while this component is mounted that suppresses AbortError only.
 */
function EsriGenericMap({
    mode = "view",
    basemap = "topo-vector",
    center = [-9.14, 38.72],
    zoom = 8,

    // If not provided by the caller, fallback to runtime config (loaded from appsettings.json)
    portalURL = EsriPortalUrl,
    portalMapId = EsriPortalMapId,
    portalSceneId = EsriPortalSceneId,

    // If not provided by the caller, fallback to runtime config (loaded from appsettings.json)
    apiKey = EsriApiKey,
    portalToken,
    locale = "pt-PT",

    widgets,
    layers,
    constraints,
    filters,

    onMapClick,
    featureInfoLayers,
    onFeatureInfo,
    onComponentReady,

    locations,
    showLocations,
    enableLocationPick,
    onLocationPick,

    onReverseGeocode,

    // Debounce mapConfig application to prevent "configChanged: true" spam + AbortError
    // Recommended values: 120..250 (ms) for heavy pages; 0 keeps previous behaviour
    configDebounceMs = 180,

    style,
    className,
}) {
    /* =====================================================================================================================
     * Element instance identity / remount control
     * =================================================================================================================== */

    /**
     * baseElementIdRef
     *
     * Holds a stable random prefix for the DOM id of this <esri-map> instance.
     * Keep this stable across re-renders so the element id is deterministic for debugging.
     */
    const baseElementIdRef = useRef(
        `esri-map-${Math.random().toString(16).slice(2)}`
    );

    /**
     * elementId
     *
     * Stable element id for this instance.
     *
     * IMPORTANT:
     * - DONOT remount the <esri-map> element on locale changes.
     * - Remounting can race with internal async WebGL/canvas work and cause
     *   null-canvas crashes (ex: clearRect on null) on some stacks.
     *
     * Instead:
     * - locale is applied via mapConfig updates.
     * - If a specific build requires remount for locale, that should be handled
     *   higher-level and only when strictly necessary.
     */
    const elementId = useMemo(() => `${baseElementIdRef.current}`, []);

    /* =====================================================================================================================
     * Core refs for element, component API, and lifecycle coordination
     * =================================================================================================================== */

    /**
     * domElRef
     *
     * Reference to the <esri-map> DOM element.
     * This is where assign `el.mapConfig = mapConfig`.
     */
    const domElRef = useRef(null);

    /**
     * componentRef
     *
     * Reference to the component API instance provided by the web component via onViewReady.
     * This API is expected to expose:
     * - addMapCoordinatesListener
     * - addMapFeaturesInfoListener
     * - addMapReverseGeocodingListener
     * - mapAction (optional)
     * And may expose the underlying ArcGIS view object.
     */
    const componentRef = useRef(null);

    /**
     * viewReadyRef
     *
     * Indicates whether onViewReady has fired for the current component instance.
     * Use this to avoid syncing listeners before the API instance exists.
     */
    const viewReadyRef = useRef(false);

    /**
     * applySeqRef
     *
     * Sequence token for mapConfig application.
     *
     * Why:
     * - Debounce assignment and wait on customElements.whenDefined.
     * - Async completion can arrive "late" after newer renders happened.
     * - This token lets us ignore late completions from older renders.
     */
    const applySeqRef = useRef(0);

    /**
     * destroyRequestedRef
     *
     * Set to true when start destroying/unmounting the current element.
     *
     * Why:
     * - onViewReady may fire late (after unmount/remount).
     * - If that happens, immediately destroy the received component instance
     *   to avoid leaking WebGL contexts or leaving an orphaned view running.
     */
    const destroyRequestedRef = useRef(false);

    /**
     * lastSignatureRef
     *
     * Stores the last deterministic signature that was applied to the current element.
     * Used to skip redundant mapConfig assignments (expensive).
     */
    const lastSignatureRef = useRef(null);

    /**
     * lastAppliedElRef
     *
     * Tracks which DOM element instance received lastSignatureRef.
     *
     * Why:
     * - When React remounts <esri-map>, domElRef changes to a new node.
     * - Even if signature is identical, the new element must receive mapConfig at least once.
     */
    const lastAppliedElRef = useRef(null);

    /**
     * handlesRef
     *
     * Stores listener handle references returned by the component API.
     * Each handle is expected to implement remove().
     *
     * Keep these in a ref so:
     * - removing listeners does not trigger re-renders
     * - cleanup is centralized and deterministic
     */
    const handlesRef = useRef({
        coordinates: null,
        genericFeaturesInfo: null,
        locationsFeaturesInfo: null,
        reverseGeocode: null,
    });

    /* =====================================================================================================================
     * AbortError detection and suppression
     * =================================================================================================================== */

    /**
     * isAbortError
     *
     * Detects AbortError-like failures across different environments and Esri bundles.
     *
     * Why:
     * - ArcGIS frequently cancels async work (hitTest, popup feature fetch, tile prefetch, etc.).
     * - Cancelled work often rejects Promises with AbortError (or "Aborted").
     * - In Esri bundles, the error can be wrapped (ex: Error: $je { name:'AbortError', message:'Aborted' })
     *   and the actual payload may live under nested properties.
     *
     * This is expected control flow (cancellation), not a fatal error.
     */
    const isAbortError = useCallback((e) => {
        if (!e) return false;

        // Some Esri builds wrap the real error object inside other properties.
        // Try common "unwrap" shapes observed in practice.
        const unwrap = (x) =>
            x?.n ?? x?.error ?? x?.reason ?? x?.originalError ?? x;

        const err = unwrap(e);

        // Direct AbortError / DOMException form
        const name = err?.name ?? e?.name;
        if (name === "AbortError") return true;

        // Message-based detection (covers "Aborted", "AbortError", and similar)
        const msg = String(err?.message ?? e?.message ?? err ?? e ?? "")
            .toLowerCase()
            .trim();

        // Exact "Aborted" case (very common in ArcGIS cancellations)
        if (msg === "aborted") return true;

        // Any abort-like message (covers "AbortError", "The operation was aborted", etc.)
        if (msg.includes("abort")) return true;

        // Specific ArcGIS teardown/cancel message you observed:
        // "AnalysisViewManager was destroyed"
        if (msg.includes("analysisviewmanager was destroyed")) return true;

        return false;
    }, []);

    /**
     * Scoped suppression for AbortError noise (unhandledrejection + error)
     *
     * IMPORTANT:
     * - Installed only while this component is mounted (local to the map wrapper).
     * - capture: true to intercept before other handlers / dev overlays.
     * - Suppress ONLY abort-like cancellations; all other errors still propagate.
     *
     * Extra note:
     * - Some dev overlays keep showing errors even if preventDefault is called.
     *   Attaching a no-op catch to the promise can help in those environments.
     */
    useLayoutEffect(() => {
        const isAbortLike = (reasonOrError) => isAbortError(reasonOrError);

        const onUnhandledRejection = (evt) => {
            if (!isAbortLike(evt?.reason)) return;

            // Best-effort suppression
            try {
                evt.preventDefault?.();
            } catch { }
            try {
                evt.stopImmediatePropagation?.();
            } catch { }
            try {
                evt.stopPropagation?.();
            } catch { }

            // Some overlays still react unless the promise is handled.
            // This attaches a no-op catch to ensure the rejection is "observed".
            try {
                if (evt?.promise && typeof evt.promise.catch === "function") {
                    evt.promise.catch(() => { });
                }
            } catch { }
        };

        const onError = (evt) => {
            // Some runtimes surface AbortError as an ErrorEvent instead of unhandledrejection
            const err = evt?.error ?? evt?.message ?? evt;
            if (!isAbortLike(err)) return;

            // Best-effort suppression
            try {
                evt.preventDefault?.();
            } catch { }
            try {
                evt.stopImmediatePropagation?.();
            } catch { }
            try {
                evt.stopPropagation?.();
            } catch { }
        };

        // Capture phase = true (important for intercepting before dev overlays)
        window.addEventListener("unhandledrejection", onUnhandledRejection, true);
        document.addEventListener("unhandledrejection", onUnhandledRejection, true);

        window.addEventListener("error", onError, true);
        document.addEventListener("error", onError, true);

        return () => {
            window.removeEventListener("unhandledrejection", onUnhandledRejection, true);
            document.removeEventListener("unhandledrejection", onUnhandledRejection, true);

            window.removeEventListener("error", onError, true);
            document.removeEventListener("error", onError, true);
        };
    }, [isAbortError]);


    /**
     * CRA / React Dev Overlay suppression
     *
     * Some AbortErrors are logged via console.error / console.warn internally
     * by ArcGIS / web components. The dev overlay treats those as fatal.
     *
     * Suppress ONLY Abort-like logs while this component is mounted.
     */
    useLayoutEffect(() => {
        const origError = console.error;
        const origWarn = console.warn;

        const isAbortLikeArg = (arg) => {
            if (!arg) return false;

            // Native AbortError / DOMException
            if (isAbortError(arg)) return true;

            // string logs
            if (typeof arg === "string") {
                const s = arg.toLowerCase();
                if (s === "aborted") return true;
                if (s.includes("abort")) return true;
            }

            // objects with message
            const msg = String(arg?.message ?? "").toLowerCase();
            if (msg === "aborted") return true;
            if (msg.includes("abort")) return true;

            return false;
        };

        const shouldSuppress = (args) => args.some(isAbortLikeArg);

        console.error = (...args) => {
            if (shouldSuppress(args)) return;
            origError(...args);
        };

        console.warn = (...args) => {
            if (shouldSuppress(args)) return;
            origWarn(...args);
        };

        return () => {
            console.error = origError;
            console.warn = origWarn;
        };
    }, [isAbortError]);

    /* =====================================================================================================================
     * Normalize runtime config inputs
     * =================================================================================================================== */

    /**
     * The web component / ArcGIS initialization code can treat empty strings as "provided but invalid".
     * That can trigger internal re-initialization loops and WebGL churn.
     * Normalize empty strings into undefined so they behave as "not provided".
     */
    const resolvedPortalMapId = useMemo(() => {
        const v = typeof portalMapId === "string" ? portalMapId.trim() : portalMapId;
        return typeof v === "string" && v.length > 0 ? v : undefined;
    }, [portalMapId]);

    const resolvedPortalSceneId = useMemo(() => {
        const v =
            typeof portalSceneId === "string"
                ? portalSceneId.trim()
                : portalSceneId;
        return typeof v === "string" && v.length > 0 ? v : undefined;
    }, [portalSceneId]);

    const resolvedApiKey = useMemo(() => {
        const v = typeof apiKey === "string" ? apiKey.trim() : apiKey;
        return typeof v === "string" && v.length > 0 ? v : undefined;
    }, [apiKey]);

    const resolvedPortalToken = useMemo(() => {
        const v =
            typeof portalToken === "string" ? portalToken.trim() : portalToken;
        return typeof v === "string" && v.length > 0 ? v : undefined;
    }, [portalToken]);

    /* =====================================================================================================================
     * Listener handle cleanup helpers
     * =================================================================================================================== */

    /**
     * safeRemoveHandle
     *
     * Best-effort removal of a single listener handle.
     * Never throws; cleanup must not break unmount logic.
     */
    const safeRemoveHandle = useCallback((h) => {
        try {
            if (h && typeof h.remove === "function") h.remove();
        } catch {
            // Best-effort cleanup must never throw.
        }
    }, []);

    /**
     * safeRemoveAllHandles
     *
     * Removes all listeners that were registered through the component API and clears references.
     * This prevents duplicate listeners on reconfiguration and prevents leaks on unmount.
     */
    const safeRemoveAllHandles = useCallback(() => {
        safeRemoveHandle(handlesRef.current.coordinates);
        safeRemoveHandle(handlesRef.current.genericFeaturesInfo);
        safeRemoveHandle(handlesRef.current.locationsFeaturesInfo);
        safeRemoveHandle(handlesRef.current.reverseGeocode);

        handlesRef.current.coordinates = null;
        handlesRef.current.genericFeaturesInfo = null;
        handlesRef.current.locationsFeaturesInfo = null;
        handlesRef.current.reverseGeocode = null;
    }, [safeRemoveHandle]);

    /* =====================================================================================================================
     * Defensive destruction helpers
     * =================================================================================================================== */

    /**
     * safeDestroyAny
     *
     * Best-effort destruction of ArcGIS-related objects.
     *
     * Supports common shapes:
     * - obj.view.destroy()
     * - obj.destroy()
     * - obj.dispose()
     *
     * Why:
     * - WebGL + ArcGIS views can leak if not explicitly destroyed during unmount/remount.
     * - Different builds or versions can expose different destroy APIs.
     */
    const safeDestroyAny = useCallback((obj) => {
        if (!obj) return;

        /**
         * IMPORTANT CHANGE (Jan 2026):
         * - Prefer a "gentle" teardown before destroy:
         *   detach the view container so pending canvas work can no-op safely.
         * - This helps avoid null-canvas crashes (e.g., clearRect on null) in some stacks.
         */
        try {
            if (obj.view && "container" in obj.view) obj.view.container = null;
        } catch { }

        // Try to destroy underlying view first (common in ArcGIS patterns).
        try {
            if (obj.view && typeof obj.view.destroy === "function") obj.view.destroy();
        } catch { }

        // Try generic destroy hook.
        try {
            if (typeof obj.destroy === "function") obj.destroy();
        } catch { }

        // Try generic dispose hook.
        try {
            if (typeof obj.dispose === "function") obj.dispose();
        } catch { }
    }, []);

    /**
     * safeDestroyCurrent
     *
     * Performs cleanup for the current map instance during unmount.
     *
     * IMPORTANT:
     * - Destroy the ArcGIS view / component API.
     * - DO NOT clear el.mapConfig during teardown.
     * - DO NOT forcibly destroy the <esri-map> DOM element itself.
     *
     * Reason:
     * - The web component may still have internal async tasks (image loads,
     *   workers, canvas draws).
     * - Clearing mapConfig (or destroying the element) can race with internal async
     *   canvas operations and cause errors like:
     *     "Cannot read properties of null (reading 'clearRect')".
     *
     * Let React handle the DOM node lifecycle safely, and let the web component
     * settle its internal async work while we've already detached/destroyed the view.
     */
    const safeDestroyCurrent = useCallback(() => {
        // 1) Remove all listeners registered via the component API.
        safeRemoveAllHandles();

        // 2) Destroy the ArcGIS view / component API.
        safeDestroyAny(componentRef.current);

        // 3) IMPORTANT: intentionally do NOT set el.mapConfig = null here.

        // 4) Reset internal refs for a clean next mount.
        componentRef.current = null;
        viewReadyRef.current = false;
    }, [safeDestroyAny, safeRemoveAllHandles]);

    /* =====================================================================================================================
     * Props normalization (widgets/layers/center)
     * =================================================================================================================== */

    /**
     * normalizedWidgetsObject
     *
     * Normalizes the incoming `widgets` prop into the object shape expected by the web component.
     *
     * Supported inputs:
     * - undefined/null -> undefined
     * - object -> copied with trimmed keys
     * - string[] -> converted to { [name]: true }
     */
    const normalizedWidgetsObject = useMemo(() => {
        if (!widgets) return undefined;

        // Object form: normalize keys but preserve values.
        if (widgets && typeof widgets === "object" && !Array.isArray(widgets)) {
            const out = {};
            Object.keys(widgets).forEach((k) => {
                const key = String(k || "").trim();
                if (!key) return;
                out[key] = widgets[k];
            });
            return out;
        }

        // Array form: enable widget names.
        if (Array.isArray(widgets)) {
            const out = {};
            widgets
                .map((w) => String(w || "").trim())
                .filter(Boolean)
                .forEach((name) => {
                    out[name] = true;
                });
            return out;
        }

        return undefined;
    }, [widgets]);

    /**
     * resolvedShowLocations
     *
     * Determines whether the internal "Locations" overlay should be added.
     *
     * Rules:
     * - If showLocations prop is explicitly boolean, it wins.
     * - Otherwise, show Locations when `locations` is a non-empty array.
     */
    const resolvedShowLocations =
        typeof showLocations === "boolean"
            ? showLocations
            : Array.isArray(locations) && locations.length > 0;

    /**
     * resolvedEnableLocationPick
     *
     * Determines whether clicking a Locations feature should call onLocationPick.
     *
     * Rules:
     * - If enableLocationPick prop is explicitly boolean, it wins.
     * - Otherwise, enable only in create/update and when onLocationPick is provided.
     */
    const resolvedEnableLocationPick =
        typeof enableLocationPick === "boolean"
            ? enableLocationPick
            : (mode === "create" || mode === "update") &&
            typeof onLocationPick === "function";

    /**
     * locationsGeoJson
     *
     * Converts `locations` input to a GeoJSON FeatureCollection used by the internal Locations overlay.
     * Filters out invalid coordinate entries.
     */
    const locationsGeoJson = useMemo(() => {
        if (
            !resolvedShowLocations ||
            !Array.isArray(locations) ||
            locations.length === 0
        )
            return null;

        const features = locations
            .filter((l) => Number.isFinite(l.longitude) && Number.isFinite(l.latitude))
            .map((l) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [Number(l.longitude), Number(l.latitude)],
                },
                properties: {
                    id: l.id,
                    code: l.code,
                    name: l.name,
                    countryId: l.countryId,
                },
            }));

        return { type: "FeatureCollection", features };
    }, [locations, resolvedShowLocations]);

    /**
     * locationsLayer
     *
     * Creates the internal layer definition used by the web component when locationsGeoJson is available.
     */
    const locationsLayer = useMemo(() => {
        if (!locationsGeoJson) return null;
        return { title: "Locations", type: "geojson", geojson: locationsGeoJson };
    }, [locationsGeoJson]);

    /**
     * mergedLayers
     *
     * Merges internal layers (Locations overlay) with caller-provided layers.
     *
     * Note:
     * - Internal layers are placed first so external layers can render above if desired.
     * - This also keeps "Locations" title predictable for selection/pick listeners.
     */
    const mergedLayers = useMemo(() => {
        const external = Array.isArray(layers) ? layers : [];
        const internal = locationsLayer ? [locationsLayer] : [];
        return [...internal, ...external];
    }, [layers, locationsLayer]);

    /**
     * stableCenter
     *
     * Normalizes center to numeric tuple when provided as an array.
     * This reduces accidental churn from string numbers.
     */
    const stableCenter = useMemo(() => {
        if (Array.isArray(center) && center.length >= 2) {
            return [Number(center[0]), Number(center[1])];
        }
        return center;
    }, [center]);

    /* =====================================================================================================================
     * Marker signature extraction for cheap layer movement detection
     * =================================================================================================================== */

    /**
     * getLayerMarkerSig
     *
     * Extracts a lightweight signature for simple GeoJSON point layers:
     * - Reads only features[0].geometry.coordinates when geometry is Point
     * - Rounds to 6 decimals to reduce float noise
     *
     * Why:
     * - Deep stringifying GeoJSON into configSignature is too expensive.
     * - But still want configSignature to detect marker movement so the map can update the marker.
     */
    const getLayerMarkerSig = useCallback((layer) => {
        try {
            const f0 = layer?.geojson?.features?.[0];
            if (!f0) return null;

            const g = f0?.geometry;
            if (g?.type !== "Point") return null;

            const coords = g?.coordinates;
            if (!Array.isArray(coords) || coords.length < 2) return null;

            const lon = Number(coords[0]);
            const lat = Number(coords[1]);
            if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

            // Round to avoid signature churn from tiny floating point differences.
            const r = (n) => Math.round(n * 1_000_000) / 1_000_000;
            return [r(lon), r(lat)];
        } catch {
            return null;
        }
    }, []);

    /* =====================================================================================================================
     * Stable callback plumbing (avoid stale closures)
     * =================================================================================================================== */

    /**
     * latestRef
     *
     * Stores the latest incoming callbacks and relevant flags in a ref.
     *
     * Why:
     * - mapConfig should remain stable when possible (performance).
     * - But event handlers should always call the latest props callbacks.
     * - Using a ref avoids re-creating listeners or reassigning mapConfig when callbacks change.
     */
    const latestRef = useRef({});
    useEffect(() => {
        latestRef.current = {
            mode,
            onMapClick,
            featureInfoLayers,
            onFeatureInfo,
            onComponentReady,
            onLocationPick,
            onReverseGeocode,
            resolvedShowLocations,
            resolvedEnableLocationPick,
        };
    }, [
        mode,
        onMapClick,
        featureInfoLayers,
        onFeatureInfo,
        onComponentReady,
        onLocationPick,
        onReverseGeocode,
        resolvedShowLocations,
        resolvedEnableLocationPick,
    ]);

    /* =====================================================================================================================
     * Best-effort selection reset (avoid stale selection visuals)
     * =================================================================================================================== */

    /**
     * resetSelectionsBestEffort
     *
     * Clears selection state for given layer titles by issuing a "selectByAttribute" action with "1=0".
     *
     * Why:
     * - Some screens show a "Locations" overlay and users can select/pick features.
     * - When reconfiguring or reusing the component, stale selection highlights can remain.
     * - Clear selection as a best-effort operation without surfacing AbortError noise.
     *
     * IMPORTANT (Jan 2026):
     * - The web component does NOT support selectByAttribute on GeoJSON layers (like our internal "Locations").
     * - Therefore, callers should never attempt to reset selection for GeoJSON layers via this action.
     * - This function is still useful for other layer types supported by the web component (ex: service/feature layers),
     *   and we keep it to avoid regressions in other pages that rely on selection reset for those layer types.
     */
    const resetSelectionsBestEffort = useCallback(
        (component, layerTitles) => {
            if (
                !component?.mapAction ||
                !Array.isArray(layerTitles) ||
                layerTitles.length === 0
            )
                return;

            layerTitles.forEach((layer) => {
                try {
                    const p = component.mapAction("selectByAttribute", {
                        layer,
                        where: "1=0", // Always false predicate: select nothing.
                        filterGeometry: false,
                        zoomToExtent: false,
                        highlightGeometry: false,
                    });

                    // Always attach catch handler to avoid unhandled rejections.
                    if (p && typeof p.catch === "function") {
                        p.catch((e) => {
                            if (isAbortError(e)) return;
                        });
                    }
                } catch (e) {
                    if (isAbortError(e)) return;
                }
            });
        },
        [isAbortError]
    );

    /* =====================================================================================================================
     * Listener synchronization
     * =================================================================================================================== */

    /**
     * syncListeners
     *
     * Registers event listeners on the component API based on the latest props.
     *
     * Flow:
     * 1) Remove all previously registered listeners (prevent duplicates).
     * 2) Optionally reset selection for Locations overlay (avoid stale highlight).
     * 3) Conditionally register:
     *    - Coordinates click listener (create/update)
     *    - Generic feature info listener (featureInfoLayers)
     *    - Locations pick listener (when enabled)
     *    - Reverse geocode listener (when onReverseGeocode exists)
     *
     * Key design:
     * - The listener callbacks read latestRef.current to avoid stale closure issues.
     */
    const syncListeners = useCallback(
        (component) => {
            if (!component) return;

            // Step 1: Ensure no duplicates remain from a prior configuration.
            safeRemoveAllHandles();

            // Step 2: Determine which layer titles exist right now.
            const currentLayers = Array.isArray(mergedLayers) ? mergedLayers : [];
            const existingTitles = new Set(currentLayers.map((l) => l?.title).filter(Boolean));

            /**
             * Step 3: Clear selection state for Locations if it is present and enabled.
             *
             * IMPORTANT (Jan 2026):
             * - Our internal "Locations" layer is type "geojson".
             * - The web component rejects selectByAttribute for GeoJSON layers and logs:
             *     "Layer Locations is type 'geojson' and is not supported for selectByAttribute"
             * - To preserve behaviour across the app (no regressions), we keep selection reset logic
             *   but apply it ONLY when the target layer is NOT geojson.
             */
            if (
                latestRef.current.resolvedShowLocations &&
                existingTitles.has("Locations")
            ) {
                const locationsLayerDef = currentLayers.find((l) => l?.title === "Locations");

                // Only attempt selection reset when the layer type is supported by selectByAttribute.
                if (locationsLayerDef?.type && String(locationsLayerDef.type).toLowerCase() !== "geojson") {
                    resetSelectionsBestEffort(component, ["Locations"]);
                }
            }

            // Step 4: Register listeners according to the latest props and mode.
            const latest = latestRef.current;

            // Coordinates click listener: used for picking lat/lon in create/update modes.
            if (
                (latest.mode === "create" || latest.mode === "update") &&
                typeof latest.onMapClick === "function"
            ) {
                handlesRef.current.coordinates =
                    component.addMapCoordinatesListener?.((res) => {
                        // Use latestRef to ensure call the newest onMapClick handler.
                        latestRef.current.onMapClick?.({
                            lat: res.latitude,
                            lon: res.longitude,
                        });
                    }) || null;
            }

            // Generic feature info listener: used to return attributes from hit-tested features.
            if (
                Array.isArray(latest.featureInfoLayers) &&
                latest.featureInfoLayers.length > 0 &&
                typeof latest.onFeatureInfo === "function"
            ) {
                handlesRef.current.genericFeaturesInfo =
                    component.addMapFeaturesInfoListener?.(
                        latest.featureInfoLayers,
                        (res) => {
                            latestRef.current.onFeatureInfo?.(res);
                        }
                    ) || null;
            }

            // Locations pick listener: listens only to "Locations" layer and maps attributes into a stable shape.
            if (
                latest.resolvedEnableLocationPick &&
                typeof latest.onLocationPick === "function" &&
                latest.resolvedShowLocations
            ) {
                handlesRef.current.locationsFeaturesInfo =
                    component.addMapFeaturesInfoListener?.(["Locations"], (res) => {
                        const attrs = res?.attributes || {};
                        if (attrs.id == null) return;

                        latestRef.current.onLocationPick?.({
                            id: attrs.id,
                            code: attrs.code,
                            name: attrs.name,
                            countryId: attrs.countryId,
                        });
                    }) || null;
            }

            // Reverse geocode listener: used for auto-fill behavior in caller screens.
            if (typeof latest.onReverseGeocode === "function") {
                handlesRef.current.reverseGeocode =
                    component.addMapReverseGeocodingListener?.((candidate) => {
                        latestRef.current.onReverseGeocode?.(candidate);
                    }) || null;
            }
        },
        [mergedLayers, resetSelectionsBestEffort, safeRemoveAllHandles]
    );

    /* =====================================================================================================================
     * Web component readiness callback
     * =================================================================================================================== */

    /**
     * onViewReadyStable
     *
     * Called by the web component when its internal ArcGIS view and API are ready.
     *
     * Flow:
     * 1) If destroy was requested (late callback), destroy the component immediately and exit.
     * 2) Store component API in componentRef and mark viewReadyRef true.
     * 3) Sync listeners for the new component instance.
     * 4) Notify parent via onComponentReady (using latestRef to avoid stale callback).
     */
    const onViewReadyStable = useCallback(
        (component) => {
            // Late onViewReady can occur after unmount/remount; destroy immediately in that case.
            if (destroyRequestedRef.current) {
                safeDestroyAny(component);
                return;
            }

            // Store active component API.
            componentRef.current = component;
            viewReadyRef.current = true;

            // Attach listeners based on current props.
            syncListeners(component);

            // Notify parent that the component is ready.
            if (typeof latestRef.current.onComponentReady === "function") {
                latestRef.current.onComponentReady(component);
            }
        },
        [safeDestroyAny, syncListeners]
    );

    /* =====================================================================================================================
     * mapConfig creation + signature computation
     * =================================================================================================================== */

    /**
     * mapConfig
     *
     * The configuration object assigned to `el.mapConfig`.
     *
     * Important:
     * - It contains onViewReadyStable (function). The signature computation below excludes functions.
     * - Keeping this object stable (or assigning it less frequently) is critical for performance.
     */
    const mapConfig = useMemo(() => {
        return {
            portalURL,
            portalMapId: resolvedPortalMapId,
            portalSceneId: resolvedPortalSceneId,
            portalToken: resolvedPortalToken,
            apiKey: resolvedApiKey,

            locale,
            basemap,
            center: stableCenter,
            zoom,
            widgets: normalizedWidgetsObject || {},
            layers: mergedLayers,
            constraints,
            filters,
            onViewReady: onViewReadyStable,
        };
    }, [
        portalURL,
        resolvedPortalMapId,
        resolvedPortalSceneId,
        resolvedPortalToken,
        resolvedApiKey,
        locale,
        basemap,
        stableCenter,
        zoom,
        normalizedWidgetsObject,
        mergedLayers,
        constraints,
        filters,
        onViewReadyStable,
    ]);

    /**
     * configSignature
     *
     * Deterministic signature for mapConfig inputs (excluding functions).
     *
     * Why:
     * - Assigning el.mapConfig is expensive (can reconfigure the view and cancel in-flight work).
     * - Only want to assign when a "meaningful" input changes.
     *
     * Perf detail:
     * - Dont deep stringify full GeoJSON (too expensive).
     * - Instead, include a lightweight marker signature (first Point feature coords) when possible.
     */
    const configSignature = useMemo(() => {
        const layersSig = Array.isArray(mergedLayers)
            ? mergedLayers.map((l) => ({
                title: l?.title ?? null,
                type: l?.type ?? null,
                url: l?.url ?? null,
                hasGeoJson: !!l?.geojson,
                hasJson: !!l?.json,

                // Cheap marker movement detection for simple point GeoJSON layers.
                marker:
                    l?.type === "geojson" && l?.geojson ? getLayerMarkerSig(l) : null,
            }))
            : [];

        // Make widget signature stable by sorting keys and shallow-copying nested objects.
        const widgetsSig =
            normalizedWidgetsObject && typeof normalizedWidgetsObject === "object"
                ? Object.keys(normalizedWidgetsObject)
                    .sort()
                    .reduce((acc, k) => {
                        const v = normalizedWidgetsObject[k];
                        acc[k] = typeof v === "object" ? { ...v } : v;
                        return acc;
                    }, {})
                : null;

        const centerSig = Array.isArray(stableCenter)
            ? [stableCenter[0], stableCenter[1]]
            : stableCenter && typeof stableCenter === "object"
                ? { ...stableCenter }
                : stableCenter ?? null;

        const constraintsSig =
            constraints && typeof constraints === "object"
                ? { ...constraints }
                : constraints ?? null;

        return JSON.stringify({
            portalURL: portalURL ?? null,
            portalMapId: resolvedPortalMapId ?? null,
            portalSceneId: resolvedPortalSceneId ?? null,
            hasPortalToken: !!resolvedPortalToken,
            hasApiKey: !!resolvedApiKey,
            locale: locale ?? null,
            basemap: basemap ?? null,
            center: centerSig,
            zoom: zoom ?? null,
            widgets: widgetsSig,
            layers: layersSig,
            constraints: constraintsSig,
            filters: filters ?? null,
        });
    }, [
        portalURL,
        resolvedPortalMapId,
        resolvedPortalSceneId,
        resolvedPortalToken,
        resolvedApiKey,
        locale,
        basemap,
        stableCenter,
        zoom,
        normalizedWidgetsObject,
        mergedLayers,
        constraints,
        filters,
        getLayerMarkerSig,
    ]);

    /* =====================================================================================================================
     * Element ref callback: handle node replacement and cleanup
     * =================================================================================================================== */

    /**
     * setEsriEl
     *
     * React ref callback for the <esri-map> element.
     *
     * Why this is tricky:
     * - React may replace the underlying DOM node when:
     *   - the element key changes (e.g. locale remount)
     *   - conditional rendering toggles the element
     * - The old node may still have an active ArcGIS View (WebGL) and async work in flight.
     *
     * IMPORTANT BEHAVIOR:
     * - DONT destroy the custom element DOM node here.
     * - Destroying the <esri-map> element itself can race with internal async
     *   canvas operations and cause errors like:
     *     "Cannot read properties of null (reading 'clearRect')".
     *
     * Cleanup strategy on node replacement:
     * 1) Mark destroyRequestedRef so late onViewReady callbacks self-destruct.
     * 2) Remove listeners and destroy ONLY the component API / ArcGIS view.
     * 3) Clear prev.mapConfig to release internal references.
     * 4) Reset internal refs and signature tracking.
     * 5) Let React fully own the DOM node lifecycle.
     */
    const setEsriEl = useCallback(
        (node) => {
            const prev = domElRef.current;

            // A different DOM node is being attached: clean up the previous instance.
            if (prev && prev !== node) {
                destroyRequestedRef.current = true;

                // Remove all listeners registered through the component API.
                safeRemoveAllHandles();

                // Destroy the ArcGIS view / component API (safe).
                safeDestroyAny(componentRef.current);

                /**
                 * IMPORTANT CHANGE (Jan 2026):
                 * - Do NOT clear prev.mapConfig here.
                 * - Clearing mapConfig while internal async canvas work is in-flight can
                 *   crash some builds (clearRect on null).
                 *
                 * already destroyed the view/component, and let the custom element
                 * settle its internal tasks naturally.
                 */

                // Reset internal state for the incoming element.
                componentRef.current = null;
                viewReadyRef.current = false;

                // Reset signature gating so the new element always receives mapConfig.
                lastSignatureRef.current = null;
                lastAppliedElRef.current = null;
            }

            // Store the current node reference.
            domElRef.current = node || null;

            // New node attached: allow normal initialization flow.
            if (node) {
                destroyRequestedRef.current = false;

                // Force config re-application if this is a different element instance.
                if (lastAppliedElRef.current !== node) {
                    lastSignatureRef.current = null;
                    lastAppliedElRef.current = null;
                }
            }
        },
        [safeDestroyAny, safeRemoveAllHandles]
    );

    /* =====================================================================================================================
     * mapConfig assignment effect (debounced + signature gated)
     * =================================================================================================================== */

    /**
     * applyTimerRef
     *
     * Stores the current debounce timer id for mapConfig application.
     * keep it in a ref to avoid re-renders and to ensure can cancel it on updates/unmount.
     */
    const applyTimerRef = useRef(null);

    /**
     * mapConfig application effect
     *
     * This is the "core" performance logic.
     *
     * Goals:
     * - Avoid assigning `el.mapConfig = mapConfig` on every render
     * - Debounce assignment so rapid prop changes collapse into a single apply
     * - Skip assignment if nothing meaningful changed (configSignature unchanged)
     * - Avoid applying config to a destroyed/replaced element (destroyRequestedRef)
     *
     * Flow:
     * 1) Increment apply sequence token (mySeq) to invalidate older scheduled work.
     * 2) Cancel any pending timer.
     * 3) Schedule a new timer with delay=configDebounceMs.
     * 4) On timer fire:
     *    - wait for custom element definition (customElements.whenDefined)
     *    - validate this is still the latest sequence and not cancelled
     *    - ensure element exists and destroy was not requested
     *    - if signature unchanged for same element, skip
     *    - otherwise assign el.mapConfig
     * 5) Always suppress AbortError thrown by assignment or definition wait.
     */
    useEffect(() => {
        const mySeq = ++applySeqRef.current;
        let cancelled = false;

        // Cancel any previous scheduled apply.
        if (applyTimerRef.current) {
            clearTimeout(applyTimerRef.current);
            applyTimerRef.current = null;
        }

        // Normalize debounce delay.
        const delay = Number.isFinite(configDebounceMs)
            ? Math.max(0, configDebounceMs)
            : 0;

        // Schedule the apply.
        applyTimerRef.current = setTimeout(() => {
            applyTimerRef.current = null;

            // Wait until the custom element is defined before assigning mapConfig.
            customElements
                .whenDefined("esri-map")
                .then(() => {
                    // Cancel checks: this prevents late async work from applying to newer renders.
                    if (cancelled) return;
                    if (mySeq !== applySeqRef.current) return;

                    const el = domElRef.current;
                    if (!el) return;

                    // If unmount/remount cleanup started, do not apply config.
                    if (destroyRequestedRef.current) return;

                    // Skip redundant assignment if signature is unchanged for the same element instance.
                    const elementChanged = lastAppliedElRef.current !== el;
                    if (!elementChanged && lastSignatureRef.current === configSignature)
                        return;

                    // Record signature and element before assignment so can skip later repeats.
                    lastSignatureRef.current = configSignature;
                    lastAppliedElRef.current = el;

                    // Apply config. This can trigger internal cancellations; suppress AbortError.
                    try {
                        el.mapConfig = mapConfig;
                    } catch (e) {
                        if (isAbortError(e)) return;
                    }
                })
                .catch((e) => {
                    // whenDefined should rarely reject, but keep AbortError suppression consistent.
                    if (isAbortError(e)) return;
                });
        }, delay);

        return () => {
            cancelled = true;

            // Cancel the scheduled apply when dependencies change or on unmount.
            if (applyTimerRef.current) {
                clearTimeout(applyTimerRef.current);
                applyTimerRef.current = null;
            }
        };
    }, [mapConfig, configSignature, configDebounceMs, isAbortError]);

    /* =====================================================================================================================
     * Unmount cleanup
     * =================================================================================================================== */

    /**
     * Unmount cleanup effect
     *
     * Ensures that:
     * - Pending timers are cancelled
     * - Listeners and views are destroyed
     * - Signature tracking is cleared so a future mount starts cleanly
     */
    useEffect(() => {
        return () => {
            destroyRequestedRef.current = true;

            // Cancel any pending debounced apply.
            if (applyTimerRef.current) {
                clearTimeout(applyTimerRef.current);
                applyTimerRef.current = null;
            }

            // Destroy listeners, component API, and element resources.
            safeDestroyCurrent();

            // Clear signature gates.
            lastSignatureRef.current = null;
            lastAppliedElRef.current = null;
        };
    }, [safeDestroyCurrent]);

    /* =====================================================================================================================
     * Listener re-sync for prop changes (post view-ready)
     * =================================================================================================================== */

    /**
     * Listener re-sync effect
     *
     * Runs when props that influence listener behavior change.
     * This does NOT reassign mapConfig; it updates listeners on the existing component API.
     *
     * Guard:
     * - Runs only once the view has reported ready and componentRef is available.
     */
    useEffect(() => {
        const c = componentRef.current;
        if (!viewReadyRef.current || !c) return;
        syncListeners(c);
    }, [
        mode,
        featureInfoLayers,
        onFeatureInfo,
        onMapClick,
        onLocationPick,
        onReverseGeocode,
        resolvedShowLocations,
        resolvedEnableLocationPick,
        syncListeners,
    ]);

    /* =====================================================================================================================
     * Render
     * =================================================================================================================== */

    /**
     * Render container and the <esri-map> element.
     *
     * Notes:
     * - Container uses CSS containment to reduce layout/paint cost from the WebGL subtree.
     * - <esri-map> is absolutely positioned to fill the container.
     * - The key and id include instanceVersion to force a remount when locale changes.
     */
    return (
        <div
            className={className}
            style={{
                position: "relative",
                display: "block",
                height: "100%",
                width: "100%",
                minHeight: 0,
                minWidth: 0,
                contain: "layout paint size",
                overflow: "hidden",
                ...style,
            }}
        >
            <esri-map
                id={elementId}
                ref={setEsriEl}
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "block",
                    height: "100%",
                    width: "100%",
                }}
            />
        </div>
    );
}

export default memo(EsriGenericMap);
